import { Component } from '@theme/component';

const JUDGEME_ENDPOINT = 'https://judge.me/api/v1/widgets/all_reviews_page';

/** @type {[Intl.RelativeTimeFormatUnit, number][]} */
const TIME_UNITS = [
  ['year', 31536000],
  ['month', 2592000],
  ['week', 604800],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
];

const PHOTO_WIDTHS = [320, 640, 960];

/**
 * @typedef {object} Review
 * @property {string} id
 * @property {string} author
 * @property {string} body
 * @property {number} rating
 * @property {Date | null} date
 * @property {boolean} verified
 * @property {URL | null} photo
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement} grid
 * @property {HTMLElement} status
 * @property {HTMLTemplateElement} cardTemplate
 * @property {HTMLButtonElement[]} [filters]
 */

/**
 * Customer review grid fed by Judge.me's public widget API. Reviews load once the
 * section nears the viewport, and each filter chip re-queries the API.
 *
 * @extends {Component<Refs>}
 */
class CustomerReviews extends Component {
  requiredRefs = ['grid', 'status', 'cardTemplate'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {AbortController | undefined} */
  #controller;

  #relativeTime = new Intl.RelativeTimeFormat(document.documentElement.lang || 'de', { numeric: 'auto' });

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#load('all');
      },
      { rootMargin: '400px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  /**
   * @param {PointerEvent} event
   */
  selectFilter(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;

    const { filter } = button.dataset;
    if (!filter || button.getAttribute('aria-pressed') === 'true') return;

    for (const chip of this.refs.filters ?? []) {
      chip.setAttribute('aria-pressed', String(chip === button));
    }

    this.#observer?.disconnect();
    this.#load(filter);
  }

  /**
   * @param {string} filter
   */
  async #load(filter) {
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;

    const { grid } = this.refs;
    grid.setAttribute('aria-busy', 'true');
    this.#setStatus(this.dataset.loadingText);

    try {
      const reviews = await this.#fetchReviews(filter, controller.signal);
      grid.replaceChildren(...reviews.map((review) => this.#buildCard(review)));
      this.#setStatus(reviews.length ? '' : this.dataset.emptyText);
    } catch (error) {
      if (controller.signal.aborted) return;
      console.warn('[customer-reviews] loading reviews failed', error);
      grid.replaceChildren();
      this.#setStatus(this.dataset.errorText);
    } finally {
      if (this.#controller === controller) grid.removeAttribute('aria-busy');
    }
  }

  /**
   * "All" mixes photo reviews into the regular feed (every third card) so the grid
   * keeps the visual rhythm of the design; the API has no mixed sort for this.
   *
   * @param {string} filter
   * @param {AbortSignal} signal
   * @returns {Promise<Review[]>}
   */
  async #fetchReviews(filter, signal) {
    const perPage = Number(this.dataset.perPage) || 12;

    if (filter === 'photos' || filter === 'newest') {
      const sortBy = filter === 'photos' ? 'with_pictures' : 'most_recent';
      const reviews = await this.#fetchPage({ sort_by: sortBy, per_page: perPage }, signal);
      return reviews.slice(0, perPage);
    }

    // Two requests, but one failing must not blank the grid: whichever page arrived is shown.
    const [photos, others] = await Promise.allSettled([
      this.#fetchPage({ sort_by: 'with_pictures', per_page: Math.ceil(perPage / 3) }, signal),
      this.#fetchPage({ per_page: perPage }, signal),
    ]);

    if (photos.status === 'rejected' && others.status === 'rejected') throw others.reason;

    return interleave(
      photos.status === 'fulfilled' ? photos.value : [],
      others.status === 'fulfilled' ? others.value : [],
      perPage
    );
  }

  /**
   * @param {Record<string, string | number>} params
   * @param {AbortSignal} signal
   * @returns {Promise<Review[]>}
   */
  async #fetchPage(params, signal) {
    const url = new URL(JUDGEME_ENDPOINT);
    url.searchParams.set('shop_domain', this.dataset.shopDomain ?? '');
    url.searchParams.set('api_token', this.dataset.apiToken ?? '');
    url.searchParams.set('page', '1');
    for (const [key, value] of Object.entries(params)) url.searchParams.set(key, String(value));

    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Judge.me responded with ${response.status}`);

    const { all_reviews: html } = await response.json();

    // Judge.me answers some failures (revoked token, say) with a 200 and an error payload; that
    // must surface as an error, not as "no reviews".
    if (typeof html !== 'string') throw new Error('Judge.me response has no all_reviews HTML');

    const doc = new DOMParser().parseFromString(html, 'text/html');

    return [...doc.querySelectorAll('.jdgm-rev')].map(parseReview);
  }

  /**
   * @param {Review} review
   * @returns {Element}
   */
  #buildCard(review) {
    const fragment = /** @type {DocumentFragment} */ (this.refs.cardTemplate.content.cloneNode(true));
    const card = /** @type {HTMLElement} */ (fragment.firstElementChild);
    const author = shortName(review.author);

    const photo = card.querySelector('img');
    if (photo && review.photo) {
      photo.srcset = PHOTO_WIDTHS.map((width) => `${resizedPhoto(review.photo, width)} ${width}w`).join(', ');
      photo.src = resizedPhoto(review.photo, 640);
      photo.alt = (this.dataset.photoAlt ?? '').replace('[author]', author);
    } else {
      photo?.remove();
    }

    const stars = card.querySelector('[data-stars]');
    if (stars instanceof HTMLElement) stars.style.setProperty('--customer-reviews-rating', String(review.rating));
    setText(card, '[data-rating-label]', (this.dataset.ratingLabel ?? '').replace('[rating]', String(review.rating)));

    const time = card.querySelector('time');
    if (time && review.date) {
      time.dateTime = review.date.toISOString();
      time.textContent = this.#timeAgo(review.date);
    } else {
      time?.remove();
    }

    setText(card, '[data-author]', author);
    if (!review.verified) card.querySelector('[data-verified]')?.remove();

    if (/[\p{L}\p{N}]/u.test(review.body)) {
      setText(card, '[data-body]', review.body);
    } else {
      card.querySelector('[data-body]')?.remove();
    }

    return card;
  }

  /**
   * @param {Date} date
   */
  #timeAgo(date) {
    const seconds = (date.getTime() - Date.now()) / 1000;

    for (const [unit, size] of TIME_UNITS) {
      if (Math.abs(seconds) >= size) return this.#relativeTime.format(Math.trunc(seconds / size), unit);
    }

    return this.#relativeTime.format(0, 'minute');
  }

  /**
   * @param {string | undefined} text
   */
  #setStatus(text) {
    const { status } = this.refs;
    status.textContent = text ?? '';
    status.hidden = !status.textContent;
  }
}

/**
 * @param {Element} element
 * @returns {Review}
 */
function parseReview(element) {
  const pictureLink = element.querySelector('.jdgm-rev__pic-link');
  const photo = pictureLink?.getAttribute('data-mfp-src') ?? pictureLink?.getAttribute('href') ?? '';

  return {
    id: element.getAttribute('data-review-id') ?? '',
    author: element.querySelector('.jdgm-rev__author')?.textContent?.trim() ?? '',
    body: (element.querySelector('.jdgm-rev__body')?.textContent ?? '').replace(/\s+/g, ' ').trim(),
    rating: Number(element.querySelector('.jdgm-rev__rating')?.getAttribute('data-score')) || 0,
    date: parseDate(element.querySelector('.jdgm-rev__timestamp')?.getAttribute('data-content') ?? ''),
    verified: element.getAttribute('data-verified-buyer') === 'true',
    photo: parsePhotoUrl(photo),
  };
}

/**
 * Judge.me sends "2026-03-23 19:59:21 UTC", which Safari can't parse without normalising.
 *
 * @param {string} value
 */
function parseDate(value) {
  if (!value) return null;
  const date = new Date(value.replace(' ', 'T').replace(' UTC', 'Z'));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * @param {string} value
 */
function parsePhotoUrl(value) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

/**
 * @param {URL} photo
 * @param {number} width
 */
function resizedPhoto(photo, width) {
  const url = new URL(photo);
  url.searchParams.set('width', String(width));
  return url.toString();
}

/**
 * "Kirstin Schmidt" → "Kirstin S."
 *
 * @param {string} name
 */
function shortName(name) {
  const parts = name.split(/\s+/).filter(Boolean);
  const last = parts.at(-1);
  return parts.length > 1 && last ? `${parts[0]} ${last.charAt(0)}.` : name;
}

/**
 * @param {Review[]} photos
 * @param {Review[]} others
 * @param {number} limit
 */
function interleave(photos, others, limit) {
  const photoIds = new Set(photos.map((review) => review.id));
  const photoQueue = [...photos];
  const otherQueue = others.filter((review) => !photoIds.has(review.id));

  /** @type {Review[]} */
  const result = [];
  while (result.length < limit && (photoQueue.length || otherQueue.length)) {
    const preferPhoto = result.length % 3 === 0;
    const next = preferPhoto ? photoQueue.shift() ?? otherQueue.shift() : otherQueue.shift() ?? photoQueue.shift();
    if (next) result.push(next);
  }

  return result;
}

/**
 * @param {ParentNode} root
 * @param {string} selector
 * @param {string} text
 */
function setText(root, selector, text) {
  const element = root.querySelector(selector);
  if (element) element.textContent = text;
}

if (!customElements.get('customer-reviews')) {
  customElements.define('customer-reviews', CustomerReviews);
}
